import React from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import RemoveIcon from "@material-ui/icons/Remove";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import PersonOutlineOutlinedIcon from "@material-ui/icons/PersonOutlineOutlined";
import {
  isItemChangeAction,
  humanizeKey,
  formatClientIp,
  isObjectIdHex,
  filterDisplayMetadata,
} from "./ProgramLogConstants";

const useStyles = makeStyles((theme) => ({
  detailContainer: {
    padding: theme.spacing(2),
    backgroundColor: "#f8fafc",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    margin: theme.spacing(1, 0),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.25),
    },
  },
  kpiStrip: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(2),
  },
  kpiCard: {
    backgroundColor: "#ffffff",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  kpiLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  kpiValue: {
    fontSize: "1rem",
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.01em",
  },
  kpiSubtext: {
    fontSize: "0.75rem",
    color: "#475569",
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 2,
  },
  sectionTitle: {
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  tableWrapper: {
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    overflowX: "auto",
    marginBottom: theme.spacing(1.5),
  },
  subTableHeadCell: {
    padding: theme.spacing(1, 1.5),
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#64748b",
    backgroundColor: "#f1f5f9",
    borderBottom: "1px solid #e2e8f0",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    whiteSpace: "nowrap",
  },
  subTableCell: {
    padding: theme.spacing(1, 1.5),
    fontSize: "0.8125rem",
    color: "#1e293b",
    borderBottom: "1px solid #f1f5f9",
  },
  diffCard: {
    backgroundColor: "#ffffff",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    padding: theme.spacing(1, 1.25),
    marginBottom: theme.spacing(0.75),
  },
  diffItemHeader: {
    fontWeight: 600,
    fontSize: "0.8125rem",
    color: "#0f172a",
    marginBottom: theme.spacing(0.625),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  // A tight, table-like list of before/after rows sharing one bordered
  // container (rather than each field getting its own card) - the
  // compact layout for a field-diff group, used for every before/after
  // section on this page (Field Change Audit, What Changed Per Item, What
  // Changed).
  diffTable: {
    border: "1px solid #eef1f5",
    borderRadius: 6,
    backgroundColor: "#ffffff",
    overflow: "hidden",
  },
  diffTableHeadRow: {
    display: "none",
    gridTemplateColumns: "130px 1fr 18px 1fr",
    gap: theme.spacing(0.625),
    padding: theme.spacing(0.375, 1),
    backgroundColor: "#f8fafc",
    borderBottom: "1px solid #eef1f5",
    [theme.breakpoints.up("sm")]: {
      display: "grid",
    },
  },
  diffTableHeadCell: {
    fontSize: "0.65rem",
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  diffTableRow: {
    display: "grid",
    gridTemplateColumns: "1fr 18px 1fr",
    alignItems: "center",
    gap: theme.spacing(0.625),
    padding: theme.spacing(0.5, 1),
    borderBottom: "1px solid #f4f6f8",
    fontSize: "0.8rem",
    "&:last-child": {
      borderBottom: "none",
    },
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "130px 1fr 18px 1fr",
    },
  },
  diffTableFieldName: {
    fontWeight: 600,
    fontSize: "0.75rem",
    color: "#64748b",
    gridColumn: "1 / -1",
    [theme.breakpoints.up("sm")]: {
      gridColumn: "auto",
      fontSize: "0.8rem",
    },
  },
  diffValuePill: {
    borderRadius: 5,
    padding: "1px 7px",
    fontWeight: 500,
    wordBreak: "break-word",
    display: "inline-block",
    maxWidth: "100%",
  },
  diffValuePillBefore: {
    backgroundColor: "#fef2f2",
    color: "#991b1b",
    textDecoration: "line-through",
    textDecorationColor: "#fca5a5",
  },
  diffValuePillAfter: {
    backgroundColor: "#f0fdf4",
    color: "#15803d",
    fontWeight: 600,
  },
  diffArrowCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#cbd5e1",
    transform: "rotate(90deg)",
    [theme.breakpoints.up("sm")]: {
      transform: "none",
    },
  },
  // "Added"/"Removed" line marker, shown as a small chip in the item
  // header instead of eating a full before/after row.
  lineStatusChip: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "0.65rem",
    fontWeight: 700,
    padding: "2px 7px",
    borderRadius: 5,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    flexShrink: 0,
  },
  lineStatusAdded: {
    backgroundColor: "#dcfce7",
    color: "#166534",
    border: "1px solid #bbf7d0",
  },
  lineStatusRemoved: {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #fecaca",
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  metaCard: {
    backgroundColor: "#ffffff",
    padding: theme.spacing(1, 1.25),
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  metaLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  metaValue: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#0f172a",
    wordBreak: "break-word",
  },
  varianceBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    fontSize: "0.75rem",
    fontWeight: 700,
    padding: "2px 6px",
    borderRadius: 6,
  },
  variancePositive: {
    backgroundColor: "#dcfce7",
    color: "#166534",
  },
  varianceNegative: {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
  },
  varianceNeutral: {
    backgroundColor: "#f1f5f9",
    color: "#64748b",
  },
}));

export default function ProgramLogDetailView({ log, currencySymbol = "$" }) {
  const classes = useStyles();

  if (!log) return null;

  const showChanges = isItemChangeAction(log);
  const showStockCols =
    !showChanges && (log.module === "StockAdjustment" || log.module === "Item");
  // Invoice/Purchase/Stock Adjustment UPDATE logs carry per-line before/
  // after too (see server/utils/diffLineItems.js) - a line's quantity/rate/
  // cost changed, or the line was added/removed outright. Shown alongside
  // (not instead of) the Affected Items table below, which stays the
  // current/after-state view.
  const itemsWithLineChanges = !showChanges && Array.isArray(log.items)
    ? log.items.filter((it) => Array.isArray(it.changes) && it.changes.length > 0)
    : [];

  const formatMoney = (val) => {
    if (val == null || val === "") return "—";
    const num = Number(val);
    if (isNaN(num)) return String(val);
    return `${currencySymbol}${num.toFixed(2)}`;
  };

  const itemLabel = (it) => {
    if (!it) return "—";
    if (it.itemCode && it.itemCode !== it.itemId) {
      return `${it.itemName || "Unnamed"} (${it.itemCode})`;
    }
    return it.itemName || "Unnamed";
  };

  // Resolves names for diff values, ensuring raw 24-hex IDs are never shown bare
  const resolveDiffValue = (field, value, isNew = false) => {
    if (value == null || value === "") return "empty";
    const strVal = String(value).trim();
    const lowerField = String(field).toLowerCase();

    // If it's a raw 24-character hexadecimal ObjectId
    if (isObjectIdHex(strVal)) {
      if (lowerField.includes("supplier")) {
        return isNew && log.details?.supplierName
          ? log.details.supplierName
          : "Supplier";
      }
      if (lowerField.includes("client") || lowerField.includes("customer")) {
        return isNew && (log.details?.clientName || log.details?.customerName)
          ? log.details.clientName || log.details.customerName
          : "Customer";
      }
      if (lowerField.includes("user")) {
        return isNew && log.userName ? log.userName : "User";
      }
      if (lowerField.includes("category")) {
        return isNew && log.details?.category ? log.details.category : "Category";
      }
      if (lowerField.includes("unit")) {
        return "Unit";
      }
      return "—";
    }

    return renderDetailValue(field, value);
  };

  const renderDetailValue = (key, value) => {
    if (value == null || value === "") return "—";
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes("ipaddress") || lowerKey === "ip") {
      return formatClientIp(value);
    }
    if (
      typeof value === "number" &&
      (lowerKey.includes("total") ||
        lowerKey.includes("rate") ||
        lowerKey.includes("cost") ||
        lowerKey.includes("price") ||
        lowerKey.includes("amount") ||
        lowerKey.includes("paid") ||
        lowerKey.includes("balance") ||
        lowerKey.includes("tax") ||
        lowerKey.includes("vat") ||
        lowerKey.includes("discount"))
    ) {
      return formatMoney(value);
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    // If a raw 24-hex ID slips through
    if (isObjectIdHex(String(value))) {
      if (lowerKey.includes("supplier") && log.details?.supplierName) {
        return log.details.supplierName;
      }
      if ((lowerKey.includes("client") || lowerKey.includes("customer")) && (log.details?.clientName || log.details?.customerName)) {
        return log.details.clientName || log.details.customerName;
      }
      return "—";
    }
    return String(value);
  };

  // Compact before/after table for a group of changed fields - one bordered
  // container with a column header + tight divided rows, shared by every
  // diff section on this page (Field Change Audit, What Changed Per Item,
  // What Changed) so they stay visually identical.
  const renderDiffTable = (changes) => (
    <div className={classes.diffTable}>
      <div className={classes.diffTableHeadRow}>
        <span className={classes.diffTableHeadCell}>Field</span>
        <span className={classes.diffTableHeadCell}>Before</span>
        <span />
        <span className={classes.diffTableHeadCell}>After</span>
      </div>
      {changes.map((c, ci) => (
        <div key={ci} className={classes.diffTableRow}>
          <span className={classes.diffTableFieldName}>{humanizeKey(c.field)}</span>
          <span className={`${classes.diffValuePill} ${classes.diffValuePillBefore}`}>
            {resolveDiffValue(c.field, c.previousValue, false)}
          </span>
          <span className={classes.diffArrowCell}>
            <ArrowForwardIcon style={{ fontSize: 13 }} />
          </span>
          <span className={`${classes.diffValuePill} ${classes.diffValuePillAfter}`}>
            {resolveDiffValue(c.field, c.newValue, true)}
          </span>
        </div>
      ))}
    </div>
  );

  // A line-item diff (see server/utils/diffLineItems.js) leads with a
  // synthetic "Item: Added/Removed" change when the whole line is new or
  // gone - surfaced as a small header chip instead of a first table row, so
  // it reads at a glance without taking up a row of its own.
  const getLineStatus = (changes) => {
    const marker = Array.isArray(changes) ? changes[0] : null;
    if (marker?.field === "Item" && marker.newValue === "Added") return "added";
    if (marker?.field === "Item" && marker.newValue === "Removed") return "removed";
    return null;
  };

  const renderStatusChip = (status) => {
    if (!status) return null;
    return (
      <span
        className={`${classes.lineStatusChip} ${
          status === "added" ? classes.lineStatusAdded : classes.lineStatusRemoved
        }`}
      >
        {status === "added" ? "Added" : "Removed"}
      </span>
    );
  };

  const hasItems = Array.isArray(log.items) && log.items.length > 0;
  // A line removed by this edit is appended to log.items purely so the
  // diff view above can say so (see server/utils/diffLineItems.js) - it's
  // not part of the document's current state, so it's excluded from the
  // "current state" Affected Items table below.
  const isRemovedLineMarker = (it) =>
    Array.isArray(it.changes) &&
    it.changes.length === 1 &&
    it.changes[0]?.field === "Item" &&
    it.changes[0]?.newValue === "Removed";
  const currentItems = hasItems ? log.items.filter((it) => !isRemovedLineMarker(it)) : [];
  const hasCurrentItems = currentItems.length > 0;
  const fieldChanges = Array.isArray(log.details?.changes) ? log.details.changes : [];
  const detailEntries = filterDisplayMetadata(log.details || {}, log);
  const hasDetails = detailEntries.length > 0;

  // Financial summary attributes
  const details = log.details || {};
  const totalAmount = details.totalAmount ?? details.total ?? null;
  const subTotal = details.subTotal ?? null;
  const tax = details.tax ?? details.vat ?? null;
  const discount = details.discount ?? null;
  const paymentMode = details.paymentMode ?? null;
  const status = details.status ?? null;
  const supplierName = details.supplierName ?? null;
  const clientName = details.clientName ?? details.customerName ?? null;

  const hasFinancialSummary =
    totalAmount != null ||
    subTotal != null ||
    supplierName != null ||
    clientName != null ||
    status != null ||
    paymentMode != null;

  return (
    <Box className={classes.detailContainer}>
      {/* 1. High-Level Executive Summary Strip */}
      {hasFinancialSummary && (
        <div className={classes.kpiStrip}>
          {totalAmount != null && (
            <Paper elevation={0} className={classes.kpiCard}>
              <span className={classes.kpiLabel}>Total Amount</span>
              <span className={classes.kpiValue} style={{ color: "#0f766e" }}>
                {formatMoney(totalAmount)}
              </span>
            </Paper>
          )}
          {subTotal != null && (
            <Paper elevation={0} className={classes.kpiCard}>
              <span className={classes.kpiLabel}>Subtotal</span>
              <span className={classes.kpiValue}>{formatMoney(subTotal)}</span>
            </Paper>
          )}
          {tax != null && Number(tax) > 0 && (
            <Paper elevation={0} className={classes.kpiCard}>
              <span className={classes.kpiLabel}>Tax / VAT</span>
              <span className={classes.kpiValue}>{formatMoney(tax)}</span>
            </Paper>
          )}
          {discount != null && Number(discount) > 0 && (
            <Paper elevation={0} className={classes.kpiCard}>
              <span className={classes.kpiLabel}>Discount</span>
              <span className={classes.kpiValue} style={{ color: "#b91c1c" }}>
                -{formatMoney(discount)}
              </span>
            </Paper>
          )}
          {status && (
            <Paper elevation={0} className={classes.kpiCard}>
              <span className={classes.kpiLabel}>Status</span>
              <span
                style={{
                  alignSelf: "flex-start",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 6,
                  textTransform: "capitalize",
                  backgroundColor:
                    String(status).toLowerCase() === "paid"
                      ? "#dcfce7"
                      : String(status).toLowerCase() === "pending"
                      ? "#fef9c3"
                      : "#f1f5f9",
                  color:
                    String(status).toLowerCase() === "paid"
                      ? "#166534"
                      : String(status).toLowerCase() === "pending"
                      ? "#854d0e"
                      : "#475569",
                  border:
                    String(status).toLowerCase() === "paid"
                      ? "1px solid #bbf7d0"
                      : String(status).toLowerCase() === "pending"
                      ? "1px solid #fde047"
                      : "1px solid #e2e8f0",
                }}
              >
                {status}
              </span>
            </Paper>
          )}
          {paymentMode && (
            <Paper elevation={0} className={classes.kpiCard}>
              <span className={classes.kpiLabel}>Payment Mode</span>
              <span className={classes.kpiValue} style={{ fontSize: "0.875rem" }}>
                {paymentMode}
              </span>
            </Paper>
          )}
          {supplierName && (
            <Paper elevation={0} className={classes.kpiCard}>
              <span className={classes.kpiLabel}>Supplier</span>
              <span className={classes.kpiSubtext}>
                <StorefrontOutlinedIcon style={{ fontSize: 16, color: "#d97706" }} />
                <strong>{supplierName}</strong>
              </span>
            </Paper>
          )}
          {clientName && (
            <Paper elevation={0} className={classes.kpiCard}>
              <span className={classes.kpiLabel}>Customer</span>
              <span className={classes.kpiSubtext}>
                <PersonOutlineOutlinedIcon style={{ fontSize: 16, color: "#4f46e5" }} />
                <strong>{clientName}</strong>
              </span>
            </Paper>
          )}
        </div>
      )}

      {/* 2. Item Field-Level Changes (Item Module Diffs) */}
      {hasItems && showChanges && (
        <Box mb={2}>
          <Typography className={classes.sectionTitle}>
            Field Change Audit ({log.items.length} {log.items.length === 1 ? "Item" : "Items"})
          </Typography>
          {log.items.map((it, idx) => (
            <div key={idx} className={classes.diffCard}>
              <div className={classes.diffItemHeader}>
                <span>{itemLabel(it)}</span>
                {it.itemCode && (
                  <Chip
                    size="small"
                    label={`Code: ${it.itemCode}`}
                    style={{ fontSize: "0.7rem", height: 22 }}
                  />
                )}
              </div>
              {Array.isArray(it.changes) && it.changes.length > 0 ? (
                renderDiffTable(it.changes)
              ) : (
                <Typography variant="caption" style={{ color: "#94a3b8" }}>
                  No field changes recorded for this item.
                </Typography>
              )}
            </div>
          ))}
        </Box>
      )}

      {/* 3. Affected Line Items Table (Purchases, Invoices, Stock Adjustments, Returns) */}
      {hasCurrentItems && !showChanges && (
        <Box mb={2}>
          <Typography className={classes.sectionTitle}>
            <ReceiptOutlinedIcon style={{ fontSize: 16 }} />
            Affected Items ({currentItems.length})
          </Typography>
          <div className={`${classes.tableWrapper} slim-scrollbar`}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.subTableHeadCell}>Item Description</TableCell>
                  <TableCell className={classes.subTableHeadCell} align="right">
                    Qty
                  </TableCell>
                  <TableCell className={classes.subTableHeadCell}>Unit</TableCell>
                  <TableCell className={classes.subTableHeadCell} align="right">
                    Rate / Cost
                  </TableCell>
                  <TableCell className={classes.subTableHeadCell} align="right">
                    Amount
                  </TableCell>
                  {showStockCols && (
                    <>
                      <TableCell className={classes.subTableHeadCell} align="right">
                        Prev Stock
                      </TableCell>
                      <TableCell className={classes.subTableHeadCell} align="right">
                        New Stock
                      </TableCell>
                      <TableCell className={classes.subTableHeadCell} align="center">
                        Variance
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItems.map((it, idx) => {
                  const effectiveRate =
                    it.rate != null ? it.rate : it.cost != null ? it.cost : it.price != null ? it.price : null;
                  const effectiveAmount =
                    it.amount != null
                      ? it.amount
                      : it.quantity != null && effectiveRate != null
                      ? Number((it.quantity * effectiveRate).toFixed(2))
                      : null;
                  const prevStock = it.previousStock != null ? Number(it.previousStock) : null;
                  const newStock = it.newStock != null ? Number(it.newStock) : null;
                  const variance =
                    prevStock != null && newStock != null ? newStock - prevStock : null;

                  return (
                    <TableRow key={idx} hover>
                      <TableCell className={classes.subTableCell}>
                        <div style={{ fontWeight: 600 }}>{itemLabel(it)}</div>
                        {it.itemCode && it.itemCode !== it.itemId && (
                          <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                            Code: {it.itemCode}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className={classes.subTableCell} align="right">
                        <strong>{it.quantity ?? "—"}</strong>
                      </TableCell>
                      <TableCell className={classes.subTableCell}>
                        <span style={{ fontWeight: 500 }}>{it.unit || "—"}</span>
                      </TableCell>
                      <TableCell className={classes.subTableCell} align="right">
                        {effectiveRate != null ? formatMoney(effectiveRate) : "—"}
                      </TableCell>
                      <TableCell className={classes.subTableCell} align="right" style={{ fontWeight: 600 }}>
                        {effectiveAmount != null ? formatMoney(effectiveAmount) : "—"}
                      </TableCell>
                      {showStockCols && (
                        <>
                          <TableCell className={classes.subTableCell} align="right">
                            {prevStock != null ? prevStock.toFixed(2) : "—"}
                          </TableCell>
                          <TableCell className={classes.subTableCell} align="right">
                            {newStock != null ? newStock.toFixed(2) : "—"}
                          </TableCell>
                          <TableCell className={classes.subTableCell} align="center">
                            {variance != null ? (
                              <span
                                className={`${classes.varianceBadge} ${
                                  variance > 0
                                    ? classes.variancePositive
                                    : variance < 0
                                    ? classes.varianceNegative
                                    : classes.varianceNeutral
                                }`}
                              >
                                {variance > 0 ? (
                                  <TrendingUpIcon style={{ fontSize: 13 }} />
                                ) : variance < 0 ? (
                                  <TrendingDownIcon style={{ fontSize: 13 }} />
                                ) : (
                                  <RemoveIcon style={{ fontSize: 13 }} />
                                )}
                                {variance > 0 ? `+${variance.toFixed(2)}` : variance.toFixed(2)}
                              </span>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Box>
      )}

      {/* 3b. Per-Line Item Changes (Invoice/Purchase/Stock Adjustment UPDATE -
           quantity/rate/cost changed, or a line added/removed outright) */}
      {itemsWithLineChanges.length > 0 && (
        <Box mb={2}>
          <Typography className={classes.sectionTitle}>
            What Changed Per Item ({itemsWithLineChanges.length}{" "}
            {itemsWithLineChanges.length === 1 ? "Item" : "Items"})
          </Typography>
          {itemsWithLineChanges.map((it, idx) => {
            const status = getLineStatus(it.changes);
            const rows = status ? it.changes.slice(1) : it.changes;
            return (
              <div key={it.itemId || idx} className={classes.diffCard}>
                <div className={classes.diffItemHeader}>
                  <span>{itemLabel(it)}</span>
                  {renderStatusChip(status)}
                </div>
                {rows.length > 0 && renderDiffTable(rows)}
              </div>
            );
          })}
        </Box>
      )}

      {/* 4. Field Changes (Purchase/Stock Adjustment/Invoice UPDATE before/after) */}
      {fieldChanges.length > 0 && (
        <Box mb={2}>
          <Typography className={classes.sectionTitle}>
            What Changed ({fieldChanges.length} {fieldChanges.length === 1 ? "Field" : "Fields"})
          </Typography>
          {renderDiffTable(fieldChanges)}
        </Box>
      )}

      {/* 5. Details / Metadata Grid */}
      {hasDetails && (
        <Box>
          <Typography className={classes.sectionTitle}>
            Action Metadata &amp; Details
          </Typography>
          <div className={classes.detailsGrid}>
            {detailEntries.map(([key, value]) => (
              <Paper key={key} elevation={0} className={classes.metaCard}>
                <span className={classes.metaLabel}>{humanizeKey(key)}</span>
                <span className={classes.metaValue}>{renderDetailValue(key, value)}</span>
              </Paper>
            ))}
          </div>
        </Box>
      )}

      {!hasItems && !hasDetails && fieldChanges.length === 0 && !hasFinancialSummary && (
        <Typography variant="body2" style={{ color: "#94a3b8", fontStyle: "italic" }}>
          No additional item details or metadata recorded for this entry.
        </Typography>
      )}
    </Box>
  );
}

